// @flow

import * as React from 'react';
import styled from 'styled-components';
import { breakpoint } from '../../utils/responsive';

type Props = {
  position: number,
  sliderWidth: number,
  active?: boolean,
  disabled?: boolean,
};

type State = {
  pointerFocus: boolean,
};

const HandleContainer = styled.span.attrs({
  role: 'slider',
  tabIndex: '0',
})`
  ${breakpoint('large')`
    height: 18px;
    width: 18px;
    min-width: 18px;
    min-height: 18px;
    box-shadow: ${(props) =>
      props.active ? props.theme.slider.handle.boxShadow : 'none'};
  `};
  background-color: ${(props) => props.theme.slider.handle.backgroundColor};
  background-image: ${(props) => props.theme.slider.handle.backgroundImage};
  z-index: 99;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  padding: 0;
  border-radius: 42px;
  transition: none;
  pointer-events: auto;
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  display: inline-block;
  touch-action: manipulation;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  &:focus {
    outline: ${(props) => (props.pointerFocus ? 'none' : '1px dotted #212121')};
    outline: ${(props) =>
      props.pointerFocus ? 'none' : '5px auto -webkit-focus-ring-color'};
  }
`;

export default class Handle extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      pointerFocus: false,
    };
  }

  static defaultProps = {
    position: 0,
    sliderWidth: 0,
  };

  ref: HTMLSpanElement;
  width: number;

  /**** Lifecycle events ****/

  UNSAFE_componentWillReceiveProps(newProps: Props) {
    if (newProps.sliderWidth !== this.props.sliderWidth) {
      this.width = this.getWidth();
    }
  }

  componentDidUpdate() {
    /* Preserve focus from mouse/touch to allow keyboard control */
    if (this.state.pointerFocus) {
      this.ref.focus();
    }
  }

  /**** Event handlers ****/

  handlePointerFocus = () => {
    this.setState({ pointerFocus: true });
  };

  handleBlur = () => {
    if (!document.hasFocus()) return;
    this.setState({ pointerFocus: false });
  };

  /**** Helpers ****/

  getWidth() {
    if (!this.ref) return 0;
    return this.ref.offsetWidth;
  }

  /**** Render ****/

  render() {
    return (
      <HandleContainer
        {...this.props}
        /* Setting the most dynamic rules inline to prevent css flooding  */
        style={{
          left:
            this.props.position -
            (this.width / this.props.sliderWidth) * this.props.position +
            '%',
        }}
        pointerFocus={this.state.pointerFocus}
        onMouseDown={this.handlePointerFocus}
        onTouchStart={this.handlePointerFocus}
        onBlur={this.handleBlur}
        ref={(r) => {
          this.ref = r;
        }}
        data-testid="sliderHandle"
      />
    );
  }
}
